import { List, ListItem, ListItemText, Menu, TextField } from '@mui/material';
import { FC, memo, useCallback, useEffect, useState } from 'react';
import ReactGA from 'react-ga4';
import { SCREEN_NAMES } from 'src/constants/screenNames';
import { PlanningsDriverEntity } from 'src/entities/PlanningsDriver.entity';

/** 未割当センチネル。API 側で driver_id -1 は null 化される契約。 */
const UNASSIGNED_DRIVER_ID = -1;

type Props = {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onSelect: (driver: Pick<PlanningsDriverEntity, 'id' | 'name'>) => void;
  drivers: PlanningsDriverEntity[];
  /** 既に他 row で利用中の driver id 群 (= 選択肢から除外) */
  excludeDriverIds: ReadonlySet<number>;
  /** 現在この row が指している driver id (= 除外対象から外す) */
  currentDriverId: number | null;
};

const DriverSelectMenu: FC<Props> = memo(({ anchorEl, onClose, onSelect, drivers, excludeDriverIds, currentDriverId }) => {
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const [composing, setComposing] = useState(false);

  useEffect(() => {
    if (!anchorEl) {
      setInput('');
      setQuery('');
    }
  }, [anchorEl]);
  useEffect(() => { if (!composing) setQuery(input); }, [composing, input]);

  const handleSelect = useCallback((d: Pick<PlanningsDriverEntity, 'id' | 'name'>) => {
    ReactGA.event('select', {
      screen_name: SCREEN_NAMES.CONVENIENCE_DISPATCHES,
      button_name: 'ドライバー選択',
      label: d.name ?? '',
    });
    onSelect(d);
    onClose();
  }, [onSelect, onClose]);

  const filtered = drivers.filter((d) => {
    if (d.temporary) return false;
    if (d.id !== currentDriverId && excludeDriverIds.has(d.id)) return false;
    if (!query) return true;
    return (d.name ?? '').toLowerCase().includes(query.toLowerCase());
  });

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      sx={{ maxHeight: 500 }}
      slotProps={{ paper: { sx: { p: 0, m: 0 } } }}
    >
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder="検索"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onCompositionStart={() => setComposing(true)}
        onCompositionEnd={() => setComposing(false)}
      />
      <List sx={{ p: 0, m: 0, minWidth: 200 }}>
        {currentDriverId != null && currentDriverId !== UNASSIGNED_DRIVER_ID && (
          <ListItem
            key="driver-unassigned"
            component="div"
            onClick={() => handleSelect({ id: UNASSIGNED_DRIVER_ID, name: '未割当' })}
            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <ListItemText primary="未割当" primaryTypographyProps={{ fontSize: 14 }} />
          </ListItem>
        )}
        {filtered.length === 0 && (
          <ListItem sx={{ fontSize: 12, color: 'text.secondary' }}>
            <ListItemText primary="選択可能なドライバーがありません" primaryTypographyProps={{ fontSize: 12 }} />
          </ListItem>
        )}
        {filtered.map((d) => (
          <ListItem
            key={`driver-${d.id}`}
            component="div"
            onClick={() => handleSelect(d)}
            sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <ListItemText primary={d.name} primaryTypographyProps={{ fontSize: 14 }} />
          </ListItem>
        ))}
      </List>
    </Menu>
  );
});

export default DriverSelectMenu;
